import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { object, bool, func } from 'prop-types';
import cn from 'classnames';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import { FormGroup, Input } from 'ps-design';
import ReactSelect from 'ps-components/ReactSelect/ReactSelect';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import * as marketplaceMatchesActions from '../MarketplaceMatchesActions';
import './MarketplaceMatchesSlideout.scss';

const MIN_PARTICIPATION_INPUT_ERROR = 'Please input a number greater than 0';
const PLACEHOLDER_TEXT = 'e.g. 500';
const FREQUENT_TEXT = 'Only show me frequently matched matches';
const RETURN_TO_ALL = 'Return to all suggested matches';

SettingsSlideout.propTypes = {
  actions: object,
  geographies: object,
  industries: object,
  settings: object,
  showMinCompanyError: bool,
  showMinEmployeeError: bool,
  hideSlideout: func,
  slideout: object,
  onBack: func,
  isBigger: bool
};

function SettingsSlideout(props) {
  const [tempSettings, setTempSettings] = useState();

  const {
    actions,
    geographies,
    industries,
    settings,
    showMinCompanyError,
    showMinEmployeeError,
    onBack,
    slideout,
    hideSlideout,
    isBigger
  } = props;

  useEffect(() => {
    if (slideout.open || !geographies.loaded || !industries.loaded) {
      actions.getPreferences();
      actions.getGeographies();
      actions.getIndustries();
      setTempSettings(settings);
    }
  }, [props.slideout.open]);

  useEffect(() => {
    if (slideout.open) {
      savePreferencesWithoutClosing();
    }
  }, [props.settings.geographies, props.settings.industries, props.settings.onlyShowFrequent]);

  const clearedSettings = {
    geographies: [],
    industries: [],
    minCompany: null,
    minEmployee: null,
    minMatchLikelihood: 30,
    showNotPurchasedSurveys: true,
    showPurchasedSurveys: true
  };

  function handleGeographyChange(field) {
    actions.multiSelectChange(field, 'geographies');
  }

  function handleIndustryChange(field) {
    actions.multiSelectChange(field, 'industries');
  }

  function resetPreferences() {
    actions.savePreferences(tempSettings, true).then(() => (onBack ? onBack() : hideSlideout()));
  }

  function savePreferencesWithoutClosing(clear = false) {
    actions.savePreferences(clear === true ? clearedSettings : settings, clear);
  }

  function savePreferences() {
    actions.savePreferences(settings).then(() => (onBack ? onBack() : hideSlideout()));
  }

  function savePreferencesAndClose() {
    actions.savePreferences(settings).then(() => hideSlideout());
  }

  return (
    <div
      className={cn('marketplace-matches__settings-slideout', { bigger: isBigger })}
      id="marketplace-matches__settings-slideout"
    >
      <SlideoutHeader title="Marketplace Matches Filters" doClose={savePreferencesAndClose} />

      <SlideoutBody>
        {onBack && (
          <div className="return-link" onClick={onBack}>
            <i className="icon-reply" />
            {RETURN_TO_ALL}
          </div>
        )}

        <ActionableHeaderCard
          cardTitle="Preferences for Surveys"
          className="marketplace-matches__card"
          isCollapsible={false}
        >
          <div className="preferences-section">
            <div className="preferences-section__header">Participant Size</div>
            <div className="preferences-section__data-input">
              Minimum
              <FormGroup error={showMinEmployeeError ? MIN_PARTICIPATION_INPUT_ERROR : ''}>
                <Input
                  autoComplete="off"
                  className="preferences-section__input"
                  id="min-employee__input"
                  name="min-employee__input"
                  onChange={actions.editTextInput}
                  onBlur={savePreferencesWithoutClosing}
                  placeholder={PLACEHOLDER_TEXT}
                  type="number"
                  value={settings.minEmployee?.toString() || ''}
                />
              </FormGroup>
              Employees
            </div>
            <div className="preferences-section__data-input">
              Minimum
              <FormGroup error={showMinCompanyError ? MIN_PARTICIPATION_INPUT_ERROR : ''}>
                <Input
                  autoComplete="off"
                  className="preferences-section__input"
                  id="min-company__input"
                  name="min-company__input"
                  onChange={actions.editTextInput}
                  onBlur={savePreferencesWithoutClosing}
                  placeholder={PLACEHOLDER_TEXT}
                  type="number"
                  value={settings.minCompany?.toString() || ''}
                />
              </FormGroup>
              Companies
            </div>
          </div>
          <FormGroup className="preferences__select">
            <label className="preferences-section__header">Industry</label>
            <ReactSelect
              className="preferences__section-multi-select"
              isClearable={true}
              isMulti
              onChange={handleIndustryChange}
              noOptionsMessage={() => 'No results found'}
              options={industries.options}
              placeholder="Search for Industry"
              value={settings.industries}
            />
          </FormGroup>
          <FormGroup className="preferences__select">
            <label className="preferences-section__header">Geography</label>
            <ReactSelect
              className="preferences__section-multi-select"
              isClearable={true}
              isMulti
              onChange={handleGeographyChange}
              noOptionsMessage={() => 'No results found'}
              options={geographies.options}
              placeholder="Search for Geography"
              value={settings.geographies}
            />
          </FormGroup>
        </ActionableHeaderCard>

        <ActionableHeaderCard
          cardTitle="Frequently Matched"
          className="marketplace-matches__card"
          isCollapsible={false}
        >
          <div className="preferences-section__checkbox-input-container">
            <CheckboxInput
              checked={settings.onlyShowFrequent}
              id="show-purchased__checkbox"
              onClick={() => actions.toggleFrequent(!settings.onlyShowFrequent)}
            />
            <span className="preferences-section__checkbox-input-label">{FREQUENT_TEXT}</span>
          </div>
        </ActionableHeaderCard>
      </SlideoutBody>

      <SlideoutFooter isBoxShadowTheme={true}>
        <SlideoutFooterStandardButtons
          applyText="Clear Filters"
          doApply={() => savePreferencesWithoutClosing(true)}
          applyClassName="clear-button"
          doCancel={resetPreferences}
        />
        <SlideoutFooterStandardButtons
          applyText="Save"
          doApply={savePreferences}
          isApplyDisabled={showMinEmployeeError || showMinCompanyError}
          showCancel={false}
        />
      </SlideoutFooter>
    </div>
  );
}

const mapStateToProps = ({ marketPricing }) => {
  return {
    ...marketPricing.MarketplaceMatchesReducer.marketplaceMatchesReducer
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...marketplaceMatchesActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsSlideout);
